import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { productListApiService } from "../../../service/admin/productApiService";
import { categoryApiService } from "../../../service/admin/categoryApiService";
import { normalizeUrl, handleImgError } from "../../../utils/imageUtils";
import Pagination from "../../../component/library/pagination";
import { FaEdit } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { addItems, categoryData } from "../../../redux/slices/categorySlice";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";

const PAGE_SIZE_CLIENT = 20;

export default function CategoryApproval() {
    // modes: "summary" (seller list) | "detail" (products of one seller)
    const [mode, setMode] = useState("summary");
    const [sellers, setSellers] = useState([]);
    const [selectedSeller, setSelectedSeller] = useState(null);

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);

    // Category browsing
    const [catLoading, setCatLoading] = useState(false);
    const [catError, setCatError] = useState("");
    const [rootTree, setRootTree] = useState([]);
    const [showCategoryBox, setShowCategoryBox] = useState(false);
    const [browseCurrent, setBrowseCurrent] = useState([]);
    const [browseStack, setBrowseStack] = useState([]);
    const [browsePath, setBrowsePath] = useState([]);
    const [infoMsg, setInfoMsg] = useState("");
    const [activeEdit, setActiveEdit] = useState(null); // { uid, sku }

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const categoriesFromStore = useSelector(categoryData);
    const message = useMessage();

    //  If Redux already has categories, hydrate local UI tree from it
    useEffect(() => {
        if (Array.isArray(categoriesFromStore) && categoriesFromStore.length) {
            const normalized = normalizeCategoryArray(categoriesFromStore);
            setRootTree(normalized);
            setCatError("");
            setCatLoading(false);
        }
    }, [categoriesFromStore]);

    //  If Redux is empty (first time), fetch once and cache in Redux
    useEffect(() => {
        if (categoriesFromStore && Array.isArray(categoriesFromStore) && categoriesFromStore.length) return;

        let mounted = true;
        (async () => {
            try {
                setCatLoading(true);
                setCatError("");
                const res = await categoryApiService.getExistingCategory();
                if (!mounted) return;

                if (res?.status?.code === 200 && Array.isArray(res.data)) {
                    // Put into Redux (sanitized by slice)
                    dispatch(addItems(res.data));
                    // Also hydrate local normalized tree immediately
                    const normalized = normalizeCategoryArray(res.data);
                    setRootTree(normalized);
                } else {
                    setCatError(res?.error?.message || "Failed to load categories");
                }
            } catch {
                if (!mounted) return;
                setCatError("Server error while loading categories.");
            } finally {
                if (mounted) setCatLoading(false);
            }
        })();

        return () => { mounted = false; };
    }, [categoriesFromStore, dispatch]);

    const handleProduct = (product) => {
        navigate(`/product-details?ali=1&offer=${product.sku}`);
    };

    // ========= Summary Mode =========
    useEffect(() => {
        if (mode !== "summary") return;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const res = await productListApiService.getSellerSummary();
                const arr = res?.data || res || [];
                setSellers(Array.isArray(arr) ? arr : []);
            } catch (e) {
                setError(e?.message || "Failed to load sellers.");
                setSellers([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [mode]);

    // ========= Detail Mode (products of selected seller) =========
    useEffect(() => {
        if (mode !== "detail" || !selectedSeller?.mapperId) return;
        fetchProducts(selectedSeller.mapperId);
    }, [mode, selectedSeller?.mapperId]);

    const rows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE_CLIENT;
        return products.slice(start, start + PAGE_SIZE_CLIENT);
    }, [products, page]);

    const totalPagesClient = Math.max(1, Math.ceil(products.length / PAGE_SIZE_CLIENT));

    // normalize
    const normalizeNoFilter = (list = []) =>
        list.map((p, i) => {
            const uid = String(p?._id ?? p?.id ?? p?.sku ?? `row-${i}`);
            return {
                uid,
                id: p?._id ?? p?.id ?? null,
                image: normalizeUrl(
                    p?.image ||
                    (Array.isArray(p?.imgUrl) ? p?.imgUrl?.[0] : p?.imgUrl) ||
                    ""
                ),
                name: p?.name || p?.title || "",
                sku: p?.sku || "",
                categoryPath: Array.isArray(p?.categoryPath)
                    ? p?.categoryPath.filter(Boolean).join(" > ")
                    : String(p?.categoryPath || p?.category_path || p?.category?.path || "").trim(),
                is_category_approved: Boolean(p?.is_category_approved),
            };
        });

    const fetchProducts = async (mapperId) => {
        setLoading(true);
        setError("");
        setPage(1);
        try {
            const q = mapperId ? `?mapperId=${encodeURIComponent(mapperId)}` : "";
            const res = await productListApiService.getProductsWithCategoryPath(q);
            const server = (res && res.data) || res || {};

            const list = server?.products || server?.data?.products || [];
            const normalized = normalizeNoFilter(Array.isArray(list) ? list : []);

            // sort: unapproved first (false), then approved (true)
            normalized.sort((a, b) => {
                // false -> 0, true -> 1, so we invert to push false to top
                const av = a.is_category_approved ? 1 : 0;
                const bv = b.is_category_approved ? 1 : 0;
                if (av !== bv) return av - bv;
                //  tie-breaker to keep stable-ish order (by name or sku)
                return (a.name || "").localeCompare(b.name || "");
            });
            setProducts(normalized);

        } catch (e) {
            setError(e?.message || "Failed to load products.");
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };


    /** -------------------- Category fetch -------------------- */
    const normalizeCategoryArray = (arr, parentPath = "") =>
        (arr || []).map((node) => {
            const name = node?.c_name ?? node?.name ?? "";
            const nodeID =
                node?.nodeID ?? node?.nodeId ?? node?.node_id ?? node?.id ?? node?._id ?? null;
            const fullPath = parentPath ? `${parentPath} > ${name}` : name;
            const children = normalizeCategoryArray(node?.children || [], fullPath);
            return { name, nodeID, fullPath, children };
        });

    const pickNodeShape = (node) => ({
        name: node?.name,
        nodeID: node?.nodeID,
        fullPath: node?.fullPath,
        children: node?.children || [],
    });

    const ensureCategoriesLoaded = async () => {
        if (rootTree.length) return rootTree;

        if (Array.isArray(categoriesFromStore) && categoriesFromStore.length) {
            const normalized = normalizeCategoryArray(categoriesFromStore);
            setRootTree(normalized);
            return normalized;
        }

        try {
            // setCatLoading(true);
            setCatError("");
            const res = await categoryApiService.getExistingCategory();
            if (res?.status?.code === 200 && Array.isArray(res?.data)) {
                dispatch(addItems(res.data));
                const normalized = normalizeCategoryArray(res.data);
                setRootTree(normalized);
                return normalized;
            } else {
                setCatError(res?.error?.message || "Failed to load categories");
                return [];
            }
        } catch {
            setCatError("Server error while loading categories.");
            return [];
        } finally {
            setCatLoading(false);
        }
    };

    /** ------------------------ Edit Category ------------------------- */
    const openBrowseForRow = async (row) => {
        setShowCategoryBox(true);
        setActiveEdit({ uid: row.uid, sku: row.sku });
        setInfoMsg("");
        setBrowseStack([]);
        setBrowsePath([]);
        setCatLoading(true);

        const tree = await ensureCategoriesLoaded();
        setBrowseCurrent(Array.isArray(tree) ? tree : []);
        setCatLoading(false);
    };

    const onEnterNode = (node) => {
        const { children, name } = pickNodeShape(node);
        setBrowseStack((s) => [...s, browseCurrent]);
        setBrowseCurrent(children || []);
        setBrowsePath((p) => [...p, name]);
        setInfoMsg("");
    };

    const onBack = () => {
        if (!browseStack.length) {
            setShowCategoryBox(false);
            setActiveEdit(null);
            return;
        }
        const s = [...browseStack];
        const prev = s.pop();
        setBrowseStack(s);
        setBrowseCurrent(prev || []);
        setBrowsePath((p) => p.slice(0, -1));
        setInfoMsg("");
    };

    const onSelectLeaf = async (node) => {
        if (!activeEdit?.sku) return;
        const { fullPath, nodeID, children } = pickNodeShape(node);
        const isLeaf = !children || children.length === 0;
        if (!isLeaf) {
            setInfoMsg("Please select a final (leaf) category.");
            return;
        }
        if (!nodeID) {
            setInfoMsg("Invalid category node.");
            return;
        }

        try {
            const payload = {
                sku: activeEdit.sku,
                categoryId: String(nodeID),
                categoryPath: fullPath,
                is_category_approved: true,
            };
            const res = await productListApiService.updateCategoryApproval(payload);
            if (res?.status?.code === 200 || res?.data?.status?.code === 200) {
                // UI update
                setProducts((prev) =>
                    prev.map((r) =>
                        r.uid === activeEdit.uid
                            ? {
                                ...r,
                                categoryPath: fullPath,
                                is_category_approved: true,
                            }
                            : r
                    )
                );
                // toast success
                message.showToast("Category updated successfully", ToastTypes.SUCCESS);

                setShowCategoryBox(false);
                setActiveEdit(null);
                setInfoMsg("");
            } else {
                setInfoMsg("Server didn't confirm the update.");
                message.showToast("Server didn't confirm the update.", ToastTypes.ERROR);
            }
        } catch {
            setInfoMsg("Failed to update category. Try again.");
            message.showToast("Failed to update category. Try again.", ToastTypes.ERROR);
        }
    };

    /** --------------------------- Toggle ---------------------------- */
    const handleToggleApprove = async (row) => {
        //  save old value so we can roll back on failure
        const prevVal = row.is_category_approved;
        const nextVal = !prevVal;

        //  optimistic UI: flip immediately
        setProducts((prev) =>
            prev.map((r) =>
                r.uid === row.uid
                    ? { ...r, is_category_approved: nextVal }
                    : r
            )
        );

        try {
            const payload = {
                sku: row.sku,
                is_category_approved: nextVal,
            };
            const res = await productListApiService.updateCategoryApproval(payload);

            const ok =
                res?.status?.code === 200 ||
                res?.data?.status?.code === 200;

            if (!ok) {
                setProducts((prev) =>
                    prev.map((r) =>
                        r.uid === row.uid
                            ? { ...r, is_category_approved: prevVal }
                            : r
                    )
                );
                setInfoMsg("Toggle failed to update on server.");
            }
        } catch (err) {
            setProducts((prev) =>
                prev.map((r) =>
                    r.uid === row.uid
                        ? { ...r, is_category_approved: prevVal }
                        : r
                )
            );
            setInfoMsg("Error toggling category approval.");
        }
    };

    // ================== UI ==================
    return (
        <div className="category-approval-container">
            <div className="header-row">
                <h2>Category Approval</h2>

                {mode === "detail" && selectedSeller ? (
                    <div className="actions-row">
                        <button className="btn-secondary" onClick={() => setMode("summary")}>
                            ← Back to sellers
                        </button>
                        <div className="totals">
                            {loading ? "Loading…" : error ? "Error" : `${products.length} products`}
                        </div>
                    </div>
                ) : (
                    <div className="totals">
                        {loading ? "Loading…" : error ? "Error" : `${sellers.length} sellers found`}
                    </div>
                )}
            </div>

            {mode === "summary" ? (
                <>
                    {error && <div className="error-banner">{error}</div>}
                    {loading ? (
                        <p>Loading…</p>
                    ) : (
                        <div className="table-wrapper">
                            <table className="category-approval-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Seller ID</th>
                                        <th>Seller Name</th>
                                        <th>Email</th>
                                        <th>Total</th>
                                        <th>Approved ✅</th>
                                        <th>Pending ❌</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sellers.length > 0 ? (
                                        sellers.map((s, idx) => (
                                            <tr
                                                key={s.mapperId || idx}
                                                className="clickable-row"
                                                onClick={() => {
                                                    setSelectedSeller(s);
                                                    setMode("detail");
                                                }}
                                            >
                                                <td>{idx + 1}</td>
                                                <td>{s.mapperId || "-"}</td>
                                                <td>{s.sellerName || "Unknown"}</td>
                                                <td>{s.email || "-"}</td>
                                                <td>{s.totalProducts ?? 0}</td>
                                                <td>{s.approvedTrue ?? 0}</td>
                                                <td>{s.approvedFalse ?? 0}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" style={{ textAlign: "center", padding: 16 }}>
                                                No sellers.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            ) : (
                // ======= DETAIL MODE: product table (same as your original) =======
                <>
                    {selectedSeller && (
                        <div className="seller-subtitle">
                            Showing products for: <strong>{selectedSeller.sellerName || selectedSeller.mapperId}</strong>
                        </div>
                    )}


                    {error && <div className="error-banner">{error}</div>}

                    {loading ? (
                        <p>Loading…</p>
                    ) : (
                        <>
                            <div className="table-wrapper">
                                <table className="category-approval-table">
                                    <thead>
                                        <tr>
                                            <th>S. No</th>
                                            <th>Image</th>
                                            <th>Product Name</th>
                                            <th>SKU</th>
                                            <th>Category Path</th>
                                            <th>Change Category</th>
                                            <th>Category Approval</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length > 0 ? (
                                            rows.map((row, idx) => (
                                                <tr key={row.uid}>
                                                    <td>{(page - 1) * PAGE_SIZE_CLIENT + (idx + 1)}</td>

                                                    <td>
                                                        <div className="img-cell">
                                                            {row.image ? (
                                                                <img
                                                                    src={row.image}
                                                                    alt={row.name || "Product"}
                                                                    className="product-img cursor-pointer"
                                                                    loading="lazy"
                                                                    onError={handleImgError}
                                                                    referrerPolicy="no-referrer"
                                                                    onClick={() => handleProduct(row)}
                                                                />
                                                            ) : (
                                                                <div
                                                                    className="product-img placeholder cursor-pointer"
                                                                    onClick={() => handleProduct(row)}
                                                                >
                                                                    null
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="product-name">
                                                        <div
                                                            className="cursor-pointer"
                                                            title={row.name || "null"}
                                                            onClick={() => handleProduct(row)}
                                                        >
                                                            {row.name || "null"}
                                                        </div>
                                                    </td>
                                                    <td>{row.sku || "null"}</td>

                                                    <td className="category-only-path">
                                                        {row.is_category_approved && (
                                                            <span className="cat-check" title="Approved">✓</span>
                                                        )}
                                                        <span>{row.categoryPath || "null"}</span>
                                                    </td>

                                                    <td>
                                                        <button
                                                            className="icon-btn"
                                                            onClick={() => openBrowseForRow(row)}
                                                            title="Edit Category"
                                                            aria-label="Edit Category"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                    </td>

                                                    <td>
                                                        <label
                                                            className={`switch ${row.is_category_approved ? "on" : "off"}`}
                                                            title={row.is_category_approved ? "Approved" : "Not Approved"}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={row.is_category_approved}
                                                                onChange={() => handleToggleApprove(row)}
                                                            />
                                                            <span className="slider" />
                                                        </label>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" style={{ textAlign: "center", padding: 16 }}>
                                                    No records found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {showCategoryBox && (
                                    <>
                                        <div
                                            className="cdb-overlay"
                                            onClick={() => {
                                                setShowCategoryBox(false);
                                                setActiveEdit(null);
                                            }}
                                        />
                                        <div className="category-dropdown-box floating-center">
                                            <div className="cdb-head">
                                                <button
                                                    type="button"
                                                    className="cdb-back cdb-btn-compact"
                                                    onClick={onBack}
                                                    aria-label={browseStack.length ? "Back" : "Close"}
                                                    title={browseStack.length ? "Back" : "Close"}
                                                >
                                                    {browseStack.length ? "←" : "✕"}
                                                </button>
                                                <div className="cdb-title">Browse Categories</div>
                                                <div className="cdb-crumbs">
                                                    {browsePath.length ? browsePath.join(" > ") : "All Categories"}
                                                </div>
                                            </div>

                                            {catError ? (
                                                <div className="cdb-empty">{catError}</div>
                                            ) : catLoading ? (
                                                <div className="cdb-empty">Loading categories…</div>
                                            ) : browseCurrent.length === 0 ? (
                                                <div className="cdb-empty">No category data</div>
                                            ) : (
                                                <div className="cdb-list">
                                                    {browseCurrent.map((node) => {
                                                        const { name, children } = pickNodeShape(node);
                                                        const isLeaf = !children || children.length === 0;
                                                        return (
                                                            <div className="cdb-item" key={node.fullPath}>
                                                                <div className="cdb-name">{name}</div>
                                                                {isLeaf ? (
                                                                    <button
                                                                        type="button"
                                                                        className="cdb-select cdb-btn-compact"
                                                                        onClick={() => onSelectLeaf(node)}
                                                                        disabled={!activeEdit || catLoading}
                                                                    >
                                                                        Select
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="cdb-view cdb-btn-compact"
                                                                        onClick={() => onEnterNode(node)}
                                                                    >
                                                                        View →
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {infoMsg && <div className="cdb-info">{infoMsg}</div>}
                                        </div>
                                    </>
                                )}
                            </div>

                            {totalPagesClient > 1 && (
                                <div className="pagination-row">
                                    <Pagination
                                        pages={{
                                            currentPage: page,
                                            totalPages: totalPagesClient,
                                            totalItems: products.length,
                                        }}
                                        page={page}
                                        setPage={setPage}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
